import {
  BoxGeometry,
  Group,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
  Vector3,
  Vector4,
  WebGLRenderTarget,
} from 'three'

import {camera, getRenderer} from '../_commonObjects'
import {canvasSize} from '../_config'
import {CORNER_RADIUS} from '../modules/cube/_config'

export default class GlassEffect {
  camera: OrthographicCamera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 3000)
  cubes: Mesh[] = []
  cubeGroup: Group = new Group()
  fbo1: WebGLRenderTarget = new WebGLRenderTarget(10, 10)
  fbo2: WebGLRenderTarget = new WebGLRenderTarget(10, 10)
  pixelRatio: number = 0.5
  fboSize: Vector2 = new Vector2(10, 10)
  rectData: Vector4
  blurPlane: Mesh
  blurRadius: number = 12
  texelSize: Vector2 = new Vector2(1 / canvasSize.x, 1 / canvasSize.y)
  weights: number[] = []
  blurUniforms = {
    tDiffuse: {value: this.fbo2.texture},
    weights: {value: this.weights},
    direction: {value: new Vector2(1, 0)}, // Horizontal blur
    texelSize: {value: this.texelSize},
  }

  constructor(rectData: Vector4) {
    this.rectData = rectData
    this.camera.far = camera.far
    this.camera.near = camera.near
    this.camera.position.copy(camera.position)
    this.camera.updateProjectionMatrix()

    this.blurPlane = new Mesh(new PlaneGeometry(2, 2), new ShaderMaterial())

    // const orbitControls = new OrbitControls(this.camera, document.body);

    this.createGaussianBlur()
  }

  createGaussianBlur() {
    // Create 2 path for gaussian blur
    // Create weight array
    // blur radius is pixel number from center pixel, not diameter
    const sigma = this.blurRadius / 3 // Standard deviation
    let sum = 0

    for (let i = -this.blurRadius; i <= this.blurRadius; i++) {
      const weight = Math.exp(-(i * i) / (2 * sigma * sigma))
      this.weights.push(weight)
      sum += weight
    }

    // Normalize weights
    for (let i = 0; i < this.weights.length; i++) {
      this.weights[i]! /= sum
    }

    // Use same fragment shader and set different uniform for horizontal and vertical blur
    const fragmentShader = `
      uniform sampler2D tDiffuse;
      uniform float weights[${this.weights.length}];
      uniform vec2 direction;
      uniform vec2 texelSize;
      varying vec2 vUv;

      void main() {
        vec4 color = vec4(0.0);
        for (int i = -${this.blurRadius}; i <= ${this.blurRadius}; i++) {
          vec2 offset = direction * float(i) * texelSize;
          color += texture2D(tDiffuse, vUv + offset) * weights[i + ${this.blurRadius}];
        }
        gl_FragColor = color;
      }
    `

    this.blurPlane.material = new ShaderMaterial({
      vertexShader: `
        varying vec2 vUv;
        void main(){
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader,
      uniforms: this.blurUniforms,
    })
  }

  createSimpleCube(originalCube: Mesh) {
    // find a material from origianlCube
    const _material = originalCube.material as ShaderMaterial
    const cubeSize = 1.0 + CORNER_RADIUS * 2
    const geometry = new BoxGeometry(cubeSize, cubeSize, cubeSize)
    const material = new ShaderMaterial({
      vertexShader: _material.vertexShader,
      fragmentShader: _material.fragmentShader,
      uniforms: {
        ..._material.uniforms,
      },
      transparent: true,
      defines: {
        IS_GLASS_RENDER: '',
        ..._material.defines,
      },
    })
    const cube = new Mesh(geometry, material)

    cube.userData.originalCube = originalCube

    this.cubes.push(cube)

    this.cubeGroup.add(cube)
  }

  resize() {
    this.fboSize.set(Math.max(1, this.rectData.x * this.pixelRatio), Math.max(1, this.rectData.y * this.pixelRatio))

    this.fbo1.setSize(this.fboSize.x, this.fboSize.y)
    this.fbo2.setSize(this.fboSize.x, this.fboSize.y)

    this.texelSize.set(1 / this.fboSize.x, 1 / this.fboSize.y)

    this.camera.left = -this.rectData.x / 2
    this.camera.right = this.rectData.x / 2
    this.camera.top = this.rectData.y / 2
    this.camera.bottom = -this.rectData.y / 2
    this.camera.updateProjectionMatrix()
  }

  update(cubeGroup: Group, scale: number, groupPosition: Vector3) {
    const renderer = getRenderer()
    if (!renderer) return
    this.cubeGroup.scale.set(scale, scale, scale)
    this.cubeGroup.rotation.copy(cubeGroup.rotation)

    this.cubeGroup.position.copy(cubeGroup.position).add(groupPosition).multiplyScalar(scale)

    this.cubes.forEach(cube => {
      const originalCube = cube.userData.originalCube as Mesh
      cube.position.copy(originalCube.position)
      cube.rotation.copy(originalCube.rotation)
      cube.scale.copy(originalCube.scale)
    })

    renderer.setRenderTarget(this.fbo1)
    renderer.render(this.cubeGroup, this.camera)

    this.blurUniforms['tDiffuse'].value = this.fbo1.texture
    this.blurUniforms['direction'].value.set(1, 0) // Horizontal blur

    renderer.setRenderTarget(this.fbo2)
    renderer.render(this.blurPlane, this.camera)
    renderer.setRenderTarget(null)

    this.blurUniforms['tDiffuse'].value = this.fbo2.texture
    this.blurUniforms['direction'].value.set(0, 1) // Vertical blur

    renderer.setRenderTarget(this.fbo1)
    renderer.render(this.blurPlane, this.camera)
    renderer.setRenderTarget(null)
  }
}
